import { Coins, Clock, ShoppingBag, Gift } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import AnimatedBadge from './AnimatedBadge';
import BadgeProgress from './BadgeProgress';
import { expiryChipLabel } from './CoinSummaryCard';
import { BADGE_TIERS, type BadgeTier } from '../lib/badges';

interface RankHeroCardProps {
  /** Current loyalty tier, or null if the user has no delivered orders yet. */
  badge: BadgeTier | null;
  /** Delivered-order count (drives the rank). */
  orderCount: number;
  /** Qualified referral count. */
  referrals: number;
  /** Spendable coin balance. */
  balance: number;
  /** ₹ value of one coin (for the ≈₹ line). */
  coinValue: number;
  /** Coins expiring this month. */
  expiring: number;
  /** ISO date of this month's expiry deadline (for the "Exp in" chip). */
  expiringOn?: string | null;
}

/**
 * Signed-in hero for the Rewards page: a stat trio — Available coins (left),
 * the rank badge raised in the middle (name + orders + referrals), and coins
 * Expiring this month (right) — above the reused "Rank Journey" (BadgeProgress).
 * The two side panels are structurally identical so their icon chips line up,
 * and a soft tier-coloured wash + halo give the badge real presence.
 */
export default function RankHeroCard({
  badge,
  orderCount,
  referrals,
  balance,
  coinValue,
  expiring,
  expiringOn,
}: RankHeroCardProps) {
  const { isDark } = useTheme();
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';
  const panel = isDark ? 'bg-gray-800/40 border-gray-700/60' : 'bg-gray-50 border-gray-200/80';
  const statPill = isDark
    ? 'bg-gray-800 text-gray-300 border-gray-700'
    : 'bg-gray-100 text-gray-600 border-gray-200';
  const amberPill = isDark
    ? 'bg-amber-500/10 text-amber-300 border-amber-500/25'
    : 'bg-amber-50 text-amber-700 border-amber-200';
  const emeraldPill = isDark
    ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25'
    : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  const pill = 'inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap';

  // When unranked (0 delivered orders), show the first tier dimmed as a target.
  const shown = badge ?? BADGE_TIERS[0];

  return (
    <div>
      {/* Stat trio — independent of any outer card.
          Mobile: badge on top, Available + Expiring side-by-side below it.
          sm+: a single row (Available · badge · Expiring). */}
      <div className="relative flex flex-col items-center gap-4 sm:flex-row sm:items-start sm:justify-center sm:gap-6">
        {/* Badge centerpiece — the focal point: larger, lifted, stronger halo */}
        <div className="order-1 sm:order-2 relative flex flex-col items-center text-center shrink-0 sm:-translate-y-2">
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 w-48 h-48 rounded-full blur-2xl"
            style={{ background: `radial-gradient(circle, ${shown.glow}73, transparent 66%)` }}
          />
          <span
            className="relative z-[1] mb-2 text-[11px] font-bold uppercase tracking-wider px-3 py-0.5 rounded-full"
            style={{ background: `${shown.glow}29`, color: shown.glow, border: `1px solid ${shown.glow}66` }}
          >
            {badge ? 'Your rank' : 'Unranked'}
          </span>
          <AnimatedBadge
            src={shown.src}
            glow={shown.glow}
            size={132}
            variant="full"
            alt={`${shown.name} badge`}
            className={`relative z-[1] ${badge ? '' : 'opacity-40 grayscale'}`}
          />
          <p className="relative z-[1] mt-2 font-extrabold text-2xl leading-tight">{badge ? badge.name : 'No rank yet'}</p>
          <div className="relative z-[1] mt-2 flex flex-wrap items-center justify-center gap-2">
            <span className={`${pill} ${statPill}`}><ShoppingBag size={11} /> {orderCount.toLocaleString('en-IN')} Delivered {orderCount === 1 ? 'Order' : 'Orders'}</span>
            <span className={`${pill} ${statPill}`}><Gift size={11} /> {referrals.toLocaleString('en-IN')} {referrals === 1 ? 'referral' : 'referrals'}</span>
          </div>
        </div>

        {/* Side panels — one row on mobile, dissolve to inline (coins left / expiry right) on sm+ */}
        <div className="order-2 flex items-start justify-center gap-3 sm:contents">
          {/* Available */}
          <div className={`sm:order-1 flex flex-col items-center text-center shrink-0 w-[112px] sm:w-[116px] rounded-2xl border px-3 py-3.5 ${panel}`}>
            <div className="relative mb-2">
              <div className="absolute inset-0 rounded-2xl blur-lg" style={{ background: 'rgba(245,158,11,0.40)' }} aria-hidden />
              <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30 ring-1 ring-white/25">
                <Coins size={19} className="text-white" />
              </div>
            </div>
            <p className="text-xl font-extrabold leading-none tabular-nums">{balance.toLocaleString('en-IN')}</p>
            <p className={`mt-1 text-[11px] font-semibold uppercase tracking-wide ${sub}`}>Available</p>
            <span className={`mt-2 ${pill} ${amberPill}`}>≈ ₹{Math.floor(balance * coinValue).toLocaleString('en-IN')}</span>
          </div>

          {/* Expiring this month */}
          <div className={`sm:order-3 flex flex-col items-center text-center shrink-0 w-[112px] sm:w-[116px] rounded-2xl border px-3 py-3.5 ${panel}`}>
            <div className="relative mb-2">
              <div className="absolute inset-0 rounded-2xl blur-lg" style={{ background: 'rgba(244,63,94,0.40)' }} aria-hidden />
              <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center shadow-lg shadow-rose-500/30 ring-1 ring-white/25">
                <Clock size={19} className="text-white" />
              </div>
            </div>
            <p className="text-xl font-extrabold leading-none tabular-nums">{expiring.toLocaleString('en-IN')}</p>
            <p className={`mt-1 text-[11px] font-semibold uppercase tracking-wide ${sub}`}>Expiring</p>
            <span className={`mt-2 ${pill} ${emeraldPill}`}>Exp in {expiryChipLabel(expiringOn)}</span>
          </div>
        </div>
      </div>

      {/* Rank Journey — independent (keeps its own box) */}
      <BadgeProgress count={orderCount} />
    </div>
  );
}
